import { createElement, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import appConfig from 'config/app'
import { useAppSelector } from 'hooks/redux'
import { useSoundAlertBuckets } from 'components/lab/state/soundAlertBucketsStore'
import { soundNotificationService } from 'services/soundNotification.service'
import { playNotificationSound } from 'utils/notificationSounds'

export const useSoundNotifications = (): void => {
  const soundAlertBuckets = useSoundAlertBuckets()
  const userId = useAppSelector((state) => state.init.id)
  const audioCtxRef = useRef<AudioContext | null>(null)

  const hasBuckets = soundAlertBuckets.length > 0

  // Open the WebSocket and register a one-time click/keydown listener that
  // creates the AudioContext on the first user interaction (required by the
  // browser autoplay policy).
  useEffect(() => {
    if (!userId || !hasBuckets) {
      soundNotificationService.disconnect()
      return
    }

    const createCtx = () => {
      if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
        const AudioContext = window.AudioContext
        if (AudioContext) {
          audioCtxRef.current = new AudioContext()
          audioCtxRef.current.resume().catch(() => {})
        }
      }
    }

    window.addEventListener('click', createCtx, { once: true })
    window.addEventListener('keydown', createCtx, { once: true })

    soundNotificationService.connect(appConfig?.newApi)

    return () => {
      window.removeEventListener('click', createCtx)
      window.removeEventListener('keydown', createCtx)
      soundNotificationService.disconnect()
    }
  }, [userId, hasBuckets])

  // Subscribe / unsubscribe individual bucket rooms whenever the active set changes.
  useEffect(() => {
    if (!userId || !hasBuckets) return
    soundAlertBuckets.forEach((bucket) => soundNotificationService.subscribe(bucket))
  }, [userId, soundAlertBuckets.join(',')])

  // Play a chime and show a toast on every incoming notification.
  useEffect(() => {
    const unsubscribe = soundNotificationService.onNotification(({ orderId, bucket }) => {
      if (audioCtxRef.current) {
        playNotificationSound(audioCtxRef.current)
      }

      const messagePrefix = bucket === 'PATIENT_SMS'
        ? 'New patient SMS received for order '
        : 'New direct message received for order '

      const link = createElement(
        'a',
        { href: `/lab/orderEdit/${orderId}`, target: '_blank', rel: 'noopener noreferrer', style: { color: '#fff', textDecoration: 'underline' } },
        `#${orderId}`,
      )

      toast.info(
        createElement('span', null, messagePrefix, link),
        { autoClose: false },
      )
    })

    return unsubscribe
  }, [])
}
